import { DispatchPhoto } from './DispatchPhoto';
import { LoaderAssignment } from './LoaderAssignment';
import { uploadFile } from '@/utils/uploadFile';
/**
 * N3 detail — one truck (Phase 2 plan 09). Layout copied from DispatchSlipDetailPage (header, status pill, info cards,
 * progress bar, line cards); the slip's barcode progress becomes the load-scan table fed by the Inventory app.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, CheckCircle, Printer, Truck, Package } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { PICKUP_LABEL, SHORT_CODE_LABEL, STATUS_PILL, apiError, type DispatchFormDetail, type Pickup } from './dispatchForms.api';

type Tab = 'FACTORY' | 'WAREHOUSE' | 'counted' | 'photos';
const LAYER_LABEL = { empty: 'Empty', layer1: 'Layer 1', full: 'Full', strapped: 'Strapped' } as const;
const istTime = (iso: string) => {
  const ist = new Date(new Date(iso).getTime() + 330 * 60_000);
  return `${String(ist.getUTCHours()).padStart(2, '0')}:${String(ist.getUTCMinutes()).padStart(2, '0')}`;
};

export function DispatchFormDetailPage({ formId, onBack }: { formId?: string; onBack?: () => void } = {}) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = formId ?? routeId;
  const navigate = useNavigate();
  const [form, setForm] = useState<DispatchFormDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('FACTORY');
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [shortError, setShortError] = useState<string | null>(null);
  const [printError, setPrintError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [short, setShort] = useState({ lineId: '', unitCode: '', qty: '', code: 'A', note: '' });
  const [photoIds, setPhotoIds] = useState<string[]>([]);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/dispatch/forms/${id}`);
      setForm(res.data.data);
    } catch (err) {
      setError(apiError(err, 'Failed to load the dispatch form'));
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const unitsAt = useMemo(() => (p: Pickup) => (form?.units ?? []).filter((u) => u.pickupLocation === p), [form]);
  const counted = (form?.lines ?? []).filter((l) => !l.serialised);
  const photoCount = (p: Pickup) => Object.values(form?.photos[p] ?? {}).filter(Boolean).length;
  const pickups = [...new Set((form?.lines ?? []).map((l) => l.pickupLocation))] as Pickup[];

  const run = async (fn: () => Promise<any>, onError: (m: string) => void, fallback: string) => {
    setBusy(true);
    onError('');
    try {
      const res = await fn();
      if (res?.data?.data?.lines) {
        setForm(res.data.data);
      } else {
        await load();
      }
    } catch (err) {
      onError(apiError(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file: File) => {
    try {
      const uploaded = await uploadFile(file, { entity: 'dispatch_form', entityId: id });
      setPhotoIds((ids) => [...ids, uploaded.id]);
    } catch (e) { setShortError(apiError(e, 'Could not upload damage photo.')); }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin" size={32} /></div>;
  if (error || !form) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => onBack ? onBack() : navigate('/dispatch-forms')} className="flex items-center gap-2 mb-4 text-gray-500 hover:text-gray-700"><ArrowLeft size={16} /> Back</button>
        <div className="bg-red-50 dark:bg-red-900/20 text-red-600 p-4 rounded-lg"><AlertTriangle size={20} className="inline mr-2" />{error || 'Dispatch form not found'}</div>
      </div>
    );
  }

  const printTestCertificates = async () => {
    setPrintError(null);
    try {
      const r = await api.get(`/dispatch/forms/${form.id}/test-certificates`, { responseType: 'text' });
      const w = window.open('', '_blank');
      if (!w) {
        setPrintError('Allow pop-ups to print the test certificates.');
        return;
      }
      w.document.write(String(r.data));
      w.document.close();
    } catch (e: any) {
      let data = e?.response?.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          data = null;
        }
      }
      setPrintError(data?.error || 'Could not load the test certificates.');
    }
  };

  const pill = STATUS_PILL[form.status] ?? { label: form.status, className: 'bg-gray-100 text-gray-600' };
  const approvalText = form.approval.status === 'approved' || form.approval.status === 'overridden'
    ? `${form.approval.status === 'overridden' ? 'Owner override' : 'Accounts approved'} · ${form.approval.byName} ${form.approval.at ? istTime(form.approval.at) : ''}`.trim()
    : form.approval.status === 'held' ? `Held · ${form.approval.byName}: ${form.approval.holdReason}` : form.approval.status === 'cancelled' ? 'Cancelled' : 'Waiting for accounts approval';
  const loadable = ['approved', 'loading'].includes(form.status);
  const selectedLine = form.lines.find((l) => l.id === short.lineId);
  const tabs: Array<[Tab, string]> = [
    ['FACTORY', `Factory pick (${unitsAt('FACTORY').length})`], ['WAREHOUSE', `Warehouse pick (${unitsAt('WAREHOUSE').length})`],
    ['counted', `Counted items (${counted.length})`], ['photos', `Loading photos ${pickups.length ? Math.min(...pickups.map(photoCount)) : 0}/4`],
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => onBack ? onBack() : navigate('/dispatch-forms')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><ArrowLeft size={20} /></button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{form.number}</h1>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${pill.className}`}>{pill.label}</span>
            </div>
            <p className="text-sm text-gray-500">{`${form.vehicleNo} · ${form.driverName ?? 'No driver'} · ${form.orders.length} order${form.orders.length === 1 ? '' : 's'} · built by ${form.builtByName}`}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700">{approvalText}</span>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"><Printer size={14} />Print pick lists</button>
        </div>
      </div>

      {!['confirmed','confirming','cancelled'].includes(form.status) && <LoaderAssignment key={form.assignedLoaderId ?? 'none'} id={form.id} assignedLoaderId={form.assignedLoaderId} reload={load}/>}
      {!!form.damageReports?.length && <section className="mb-4 rounded border p-4"><h2>Reported loading damage</h2>{form.damageReports.map((r,i) => <p key={i}>{r.code}: {r.note} · {r.by}</p>)}</section>}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="text-xs text-gray-500 mb-1">Orders</div>
          {form.orders.map((o) => <p key={o.salesOrderId} className="text-sm font-medium">{`${o.number} · ${o.partyName}`}</p>)}
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="text-xs text-gray-500 mb-1">Transport</div>
          <p className="text-sm font-medium">{form.vehicleNo}</p>
          <p className="text-xs text-gray-500">{form.distanceKm != null ? `${form.distanceKm} km` : 'Distance not entered'}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="text-xs text-gray-500 mb-1">Load progress</div>
          <p className="text-sm font-medium">{`${form.confirm.loadedUnits} / ${form.units.length} scanned`}</p>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
            <div className={`h-1.5 rounded-full ${form.units.length && form.confirm.loadedUnits === form.units.length ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${form.units.length ? (form.confirm.loadedUnits / form.units.length) * 100 : 0}%` }} />
          </div>
        </div>
      </div>

      <div role="tablist" className="flex gap-2 mb-3">
        {tabs.map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
            className={`px-3 py-1.5 rounded-lg text-sm ${tab === key ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>{label}</button>
        ))}
      </div>

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 mb-6">
        {(tab === 'FACTORY' || tab === 'WAREHOUSE') && (
          <table aria-label={`${PICKUP_LABEL[tab]} pick`} className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                {['Serial', 'Product', 'Order', 'Grade', 'Packed', 'Load scan'].map((h) => <th key={h} className="px-4 py-2">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {unitsAt(tab).map((u) => (
                <tr key={u.id} className={u.loadStatus === 'loaded' ? 'bg-green-50 dark:bg-green-900/10' : u.lastScan?.result === 'refused' ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}>
                  <td className="px-4 py-2 font-mono text-xs">{u.code}</td>
                  <td className="px-4 py-2">{u.productName}</td>
                  <td className="px-4 py-2 font-mono text-xs">{u.salesOrderNumber}</td>
                  <td className="px-4 py-2">{u.grade ?? '—'}</td>
                  <td className="px-4 py-2">{u.packed ? 'QA pass' : 'No'}</td>
                  <td className="px-4 py-2">
                    {u.loadStatus === 'loaded' && u.lastScan ? `Loaded ${istTime(u.lastScan.at)}`
                      : u.loadStatus === 'short' ? 'Marked short'
                      : u.lastScan?.result === 'refused' ? `Refused · ${u.lastScan.message}` : 'Waiting'}
                  </td>
                </tr>
              ))}
              {unitsAt(tab).length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">{`Nothing to pick at ${PICKUP_LABEL[tab]}.`}</td></tr>}
            </tbody>
          </table>
        )}

        {tab === 'counted' && (
          <div className="p-4 space-y-3">
            {counted.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2"><Package size={16} className="text-gray-400" /><span className="font-medium">{l.description}</span>
                  <span className="text-xs text-gray-500">{`${PICKUP_LABEL[l.pickupLocation]} · planned ${l.plannedQty}${l.countedByName ? ` · counted by ${l.countedByName}` : ''}`}</span></div>
                <div className="flex items-center gap-2">
                  <input aria-label={`Counted ${l.description}`} type="number" min={0} max={l.plannedQty} disabled={!loadable}
                    value={counts[l.id] ?? (l.countedQty ?? '')} onChange={(e) => setCounts((c) => ({ ...c, [l.id]: e.target.value }))}
                    className="w-20 text-center border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700" />
                  <button disabled={!loadable || busy} onClick={() => run(() => api.patch(`/dispatch/forms/${form.id}/lines/${l.id}/count`, { countedQty: Number(counts[l.id] ?? l.countedQty ?? 0) }), setActionError, 'Saving the count failed')}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50">Save count</button>
                </div>
              </div>
            ))}
            {counted.length === 0 && <p className="text-sm text-gray-400">No counted items on this form.</p>}
          </div>
        )}

        {tab === 'photos' && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {pickups.map((p) => (
              <div key={p}>
                <p className="text-xs font-medium text-gray-500 mb-2">{PICKUP_LABEL[p]}</p>
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(LAYER_LABEL) as Array<keyof typeof LAYER_LABEL>).map((k) => (
                    <span key={k} className={`text-xs px-2 py-1 rounded ${form.photos[p]?.[k] ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{form.photos[p]?.[k] ? <DispatchPhoto id={form.photos[p]![k]!} label={LAYER_LABEL[k]}/> : `${LAYER_LABEL[k]} · No photo`}</span>
                  ))}
                </div>
              </div>
            ))}
            <p className="text-xs text-gray-400 md:col-span-2">Layer photos are recorded by the assigned loader.</p>
          </div>
        )}
      </div>

      {loadable && (
        <div data-testid="short-form" className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-4 mb-6 space-y-3">
          <h3 className="text-sm font-semibold">Mark short before the truck leaves</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="text-xs text-gray-500">Line
              <select aria-label="Line" value={short.lineId} onChange={(e) => setShort({ ...short, lineId: e.target.value, unitCode: '', qty: '' })} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm">
                <option value="">Pick a line</option>
                {form.lines.map((l) => <option key={l.id} value={l.id}>{`${l.description} · ${PICKUP_LABEL[l.pickupLocation]}`}</option>)}
              </select>
            </label>
            {selectedLine?.serialised ? (
              <label className="text-xs text-gray-500">Serial
                <select aria-label="Serial" value={short.unitCode} onChange={(e) => setShort({ ...short, unitCode: e.target.value })} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm">
                  <option value="">Pick a serial</option>
                  {form.units.filter((u) => u.lineId === selectedLine.id && u.loadStatus !== 'loaded').map((u) => <option key={u.id} value={u.code}>{u.code}</option>)}
                </select>
              </label>
            ) : (
              <label className="text-xs text-gray-500">Qty short
                <input aria-label="Qty short" type="number" min={1} value={short.qty} onChange={(e) => setShort({ ...short, qty: e.target.value })} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" />
              </label>
            )}
            <label className="text-xs text-gray-500">Short code
              <select aria-label="Short code" value={short.code} onChange={(e) => setShort({ ...short, code: e.target.value })} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm">
                {(Object.keys(SHORT_CODE_LABEL) as Array<keyof typeof SHORT_CODE_LABEL>).map((c) => <option key={c} value={c}>{`${c} · ${SHORT_CODE_LABEL[c]}`}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-500">Note
              <input aria-label="Short note" value={short.note} onChange={(e) => setShort({ ...short, note: e.target.value })} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-gray-500">Photo (needed for C)
              <input aria-label="Damage photo" type="file" accept="image/*" capture="environment" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} className="ml-2 text-xs" />
            </label>
            {photoIds.length > 0 && <span className="text-xs text-green-700">{`${photoIds.length} photo${photoIds.length === 1 ? '' : 's'} attached`}</span>}
            <button disabled={busy || !short.lineId} onClick={() => run(() => api.post(`/dispatch/forms/${form.id}/shorts`, {
              lineId: short.lineId, unitCode: selectedLine?.serialised ? short.unitCode || null : null, qty: selectedLine?.serialised ? null : Number(short.qty) || null,
              shortCode: short.code, note: short.note.trim() || null, photoFileIds: photoIds,
            }), setShortError, 'Marking short failed').then(() => setPhotoIds([]))}
              className="ml-auto px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50">Mark short</button>
          </div>
          {shortError && <p className="text-sm text-red-600">{shortError}</p>}
          <ul className="text-sm space-y-1">
            {form.lines.flatMap((l) => l.shorts.map((s) => (
              <li key={s.id} className="flex items-center justify-between">
                <span>{`${s.unitCode ? `…${s.unitCode.slice(-5)}` : `${s.qty} ×`} ${l.description} · ${s.code} · ${s.note ?? s.reason}`}</span>
                <button onClick={() => run(() => api.delete(`/dispatch/forms/${form.id}/shorts/${s.id}`), setShortError, 'Removing the short failed')} className="text-xs text-gray-500 hover:text-red-600">Remove</button>
              </li>
            )))}
          </ul>
          <p className="text-xs text-gray-400">Short qty returns to the SO pending pool.</p>
        </div>
      )}

      {form.result ? (
        <div data-testid="confirm-result" className="border border-green-200 dark:border-green-800 rounded-lg bg-green-50 dark:bg-green-900/20 p-4 space-y-2 text-sm">
          <p className="font-semibold flex items-center gap-2"><CheckCircle size={16} className="text-green-600" />Load confirmed</p>
          {form.result.invoices.map((i) => (
            <div key={i.invoiceId} className="space-y-0.5">
              <p className="font-medium">{`${i.number} · ${formatINR(i.totalPaise / 100)}${i.amountAApplied ? ' · Amount A only' : ''}`}</p>
              <p>{i.irn}</p><p>{i.ewayBill}</p><p>{i.tally}</p>
            </div>
          ))}
          {form.result.gatePass ? <p>{`Gate pass ${form.result.gatePass.number}`}</p> : <p>{form.result.gatePassNote}</p>}
          {form.status === 'confirmed' && <button onClick={printTestCertificates} className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Print test certificates</button>}
          {printError && <p>{printError}</p>}
          <p>{`${form.result.warranties.created} warrant${form.result.warranties.created === 1 ? 'y' : 'ies'} created`}</p>
          {[...form.result.warranties.skippedNoYears, ...form.result.consequences.stockChecks, ...form.result.consequences.packingSignals, ...form.result.consequences.problems].map((t: string) => <p key={t}>{t}</p>)}
        </div>
      ) : (
        <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <div>
            <p className="text-sm font-medium">{`Loaded ${form.confirm.loadedUnits} unit${form.confirm.loadedUnits === 1 ? '' : 's'} + ${form.confirm.countedItems} counted`}</p>
            <p className="text-xs text-gray-500">Invoice on loaded qty only</p>
            {form.confirm.blockers.map((b) => <p key={b} className="text-xs text-amber-700">{b}</p>)}
            {actionError && <p className="text-xs text-red-600">{actionError}</p>}
          </div>
          <div className="flex gap-2">
            {['awaiting_approval', 'held', 'approved'].includes(form.status) && form.confirm.loadedUnits === 0 && (
              <button onClick={() => { const reason = window.prompt('Why is this form cancelled?') ?? ''; if (reason) run(() => api.post(`/dispatch/forms/${form.id}/cancel`, { reason }), setActionError, 'Cancel failed'); }}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel form</button>
            )}
            <button disabled={!form.confirm.canConfirm || busy} onClick={() => run(() => api.post(`/dispatch/forms/${form.id}/confirm`), setActionError, 'Confirm failed')}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Truck size={16} />}Confirm load · raise invoice
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
